import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PARTNER_OFFERS, getPartnerWhatsAppUrl } from '../data/partnerOffers';
import { useI18n } from '../i18n';
import './PartnerOffersBanner.css';

const AUTO_MS = 6500;
const SWIPE_THRESHOLD = 40;

const Chevron = ({ dir = 'left' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
  </svg>
);

const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const websiteHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'website';
  }
};

const PartnerSlide = ({ offer, onNavigate }) => {
  const { t, dir } = useI18n();
  const whatsappUrl = getPartnerWhatsAppUrl(offer);
  const host = websiteHost(offer.website);

  return (
    <div className="partner-slide" dir={dir}>
      {/* Mobile — compact strip */}
      <div className="md:hidden px-3 py-2.5 pb-7">
        <div className="flex items-center gap-2.5">
          <img
            src={offer.logo}
            alt={offer.name}
            className="h-9 w-auto max-w-[64px] object-contain object-left rtl:object-right shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-[13px] font-bold tracking-tight text-slate-900 leading-tight truncate">
              {t(offer.headline)}
            </h3>
            <a
              href={offer.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.stopPropagation();
                onNavigate?.(event);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 active:text-slate-700"
            >
              {host}
              <ExternalIcon />
            </a>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-700 border border-violet-100 font-bold text-[11px] active:scale-[0.98]"
          >
            {t(offer.cta)}
            <span aria-hidden="true" className="inline-block rtl:-scale-x-100">→</span>
          </a>
        </div>
      </div>

      {/* Desktop / tablet */}
      <div className="hidden md:flex flex-row items-center gap-6 px-7 pt-5 pb-11">
        <div className="shrink-0 space-y-2.5 w-[168px]">
          <span className="inline-flex px-2 py-0.5 rounded-md bg-violet-50 border border-violet-100 text-violet-600 text-[9px] font-extrabold uppercase tracking-wide">
            {t(offer.badge)}
          </span>
          <div className="h-[76px] flex items-center">
            <img
              src={offer.logo}
              alt={offer.name}
              className={`w-auto object-contain object-left rtl:object-right ${offer.logoClassName || 'max-h-12 max-w-[148px]'}`}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <h3 className="text-[22px] font-bold tracking-tight text-slate-900 leading-snug">
            {t(offer.headline)}
          </h3>
          <p className="text-slate-600 font-semibold text-sm leading-snug">
            {t(offer.subhead)}
          </p>
          <p className="text-slate-400 font-medium text-[13px] leading-relaxed">
            {t(offer.detail)}
          </p>
        </div>

        <div className="shrink-0 flex items-center justify-center w-56 h-[92px]">
          <img
            src={offer.products}
            alt=""
            className="max-h-full max-w-full object-contain object-center pointer-events-none select-none"
          />
        </div>

        <div className="shrink-0 flex flex-col items-end gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-violet-50 text-violet-700 border border-violet-100 font-bold text-sm hover:bg-violet-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            {t(offer.cta)}
            <span aria-hidden="true" className="inline-block rtl:-scale-x-100">→</span>
          </a>
          <a
            href={offer.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.stopPropagation();
              onNavigate?.(event);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            className="inline-flex items-center justify-center gap-1.5 px-1 py-0.5 text-slate-500 hover:text-slate-800 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 rounded-md"
          >
            {t('See website')}
            <span className="font-medium text-slate-400">{host}</span>
            <ExternalIcon />
          </a>
        </div>
      </div>
    </div>
  );
};

const PartnerOffersBanner = ({ geoCountry }) => {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const dragStartX = useRef(null);
  const dragStartY = useRef(null);
  const dragDeltaX = useRef(0);
  const dragDeltaY = useRef(0);
  const didDrag = useRef(false);
  const count = PARTNER_OFFERS.length;
  const isMorocco = geoCountry === 'MA';

  const goTo = useCallback((next) => {
    setIndex(((next % count) + count) % count);
  }, [count]);

  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);
  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (!isMorocco || paused || count < 2) return undefined;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;
    const timer = window.setInterval(goNext, AUTO_MS);
    return () => window.clearInterval(timer);
  }, [isMorocco, paused, count, goNext]);

  const onPointerDown = (event) => {
    dragStartX.current = event.clientX;
    dragStartY.current = event.clientY;
    dragDeltaX.current = 0;
    dragDeltaY.current = 0;
    didDrag.current = false;
    setPaused(true);
  };

  const onPointerMove = (event) => {
    if (dragStartX.current == null) return;
    dragDeltaX.current = event.clientX - dragStartX.current;
    dragDeltaY.current = event.clientY - dragStartY.current;
    if (Math.abs(dragDeltaX.current) > 8) didDrag.current = true;
  };

  const onPointerUp = () => {
    if (dragStartX.current != null) {
      const dx = dragDeltaX.current;
      const dy = dragDeltaY.current;
      const isHorizontal = Math.abs(dx) > Math.abs(dy) * 1.15;
      if (isHorizontal && Math.abs(dx) > SWIPE_THRESHOLD) {
        if (dx > 0) goPrev();
        else goNext();
      }
    }
    dragStartX.current = null;
    dragStartY.current = null;
    setPaused(false);
  };

  const onSlideClick = (event) => {
    if (didDrag.current) {
      event.preventDefault();
      didDrag.current = false;
    }
  };

  if (!isMorocco || count === 0) return null;

  return (
    <section
      className="relative bg-white rounded-[20px] md:rounded-[28px] overflow-hidden border border-slate-200/80 shadow-sm touch-pan-y select-none"
      aria-roledescription="carousel"
      aria-label={t('Partner offers')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="overflow-hidden min-w-0" dir="ltr">
        <div
          className="partner-track transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {PARTNER_OFFERS.map((offer) => (
            <PartnerSlide key={offer.id} offer={offer} onNavigate={onSlideClick} />
          ))}
        </div>
      </div>

      {count > 1 && (
        <div
          className="absolute bottom-0.5 left-0 right-0 z-20 flex items-center justify-center gap-0.5 sm:gap-2 sm:bottom-2.5"
          dir="ltr"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={goPrev}
            className="w-6 h-6 rounded-full text-slate-400 active:bg-slate-100 sm:hover:text-slate-700 sm:hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label={t('Previous partner offer')}
          >
            <Chevron dir="left" />
          </button>
          <div className="flex items-center">
            {PARTNER_OFFERS.map((offer, i) => (
              <button
                key={offer.id}
                type="button"
                onClick={() => goTo(i)}
                className="w-5 h-6 sm:w-6 sm:h-6 flex items-center justify-center"
                aria-label={t('Show {name} offer', { name: offer.name })}
                aria-current={i === index ? 'true' : undefined}
              >
                <span
                  className={`block h-1 rounded-full transition-all ${
                    i === index ? 'w-3.5 bg-violet-500' : 'w-1 bg-slate-300'
                  }`}
                />
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={goNext}
            className="w-6 h-6 rounded-full text-slate-400 active:bg-slate-100 sm:hover:text-slate-700 sm:hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label={t('Next partner offer')}
          >
            <Chevron dir="right" />
          </button>
        </div>
      )}

      {count > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-slate-100 overflow-hidden" aria-hidden="true">
          <div
            key={index}
            className={`partner-progress h-full bg-violet-400 ${paused ? 'is-paused' : ''}`}
            style={{ animationDuration: `${AUTO_MS}ms` }}
          />
        </div>
      )}
    </section>
  );
};

export default PartnerOffersBanner;
